/**
 * Geometry for the canvas: coordinate conversion, resize maths, snapping,
 * marquee hit-testing.
 *
 * Pure functions, no Angular and no DOM. Two coordinate spaces are in play:
 *
 * - **document space** — the artboard's own px grid. Node `x`/`y` live here
 *   (relative to their parent), and so do guides and the marquee.
 * - **screen space** — what the user actually sees, after zoom and pan.
 *
 * `toDocumentPoint` / `toScreenPoint` are the only bridge between them.
 */

import { MIN_NODE_SIZE, SNAP_THRESHOLD_PX } from '../design-builder.constants';
import type {
  AlignmentGuide,
  DesignNode,
  Point,
  Rect,
  ResizeHandle,
} from '../design-builder.types';

export interface Viewport {
  zoom: number;
  panX: number;
  panY: number;
}

// ── Coordinate conversion ────────────────────────────────────────────────────

export function toDocumentPoint(screen: Point, viewport: Viewport): Point {
  return {
    x: (screen.x - viewport.panX) / viewport.zoom,
    y: (screen.y - viewport.panY) / viewport.zoom,
  };
}

export function toScreenPoint(doc: Point, viewport: Viewport): Point {
  return {
    x: doc.x * viewport.zoom + viewport.panX,
    y: doc.y * viewport.zoom + viewport.panY,
  };
}

/** Zoom about a fixed screen point, so the pixel under the cursor stays put. */
export function zoomAt(viewport: Viewport, nextZoom: number, anchor: Point): Viewport {
  const docAnchor = toDocumentPoint(anchor, viewport);
  return {
    zoom: nextZoom,
    panX: anchor.x - docAnchor.x * nextZoom,
    panY: anchor.y - docAnchor.y * nextZoom,
  };
}

/** Pan/zoom that fits `content` inside `viewportSize` with a margin. */
export function fitViewport(
  content: Rect,
  viewportSize: { width: number; height: number },
  margin = 48,
): Viewport {
  const availableWidth = Math.max(1, viewportSize.width - margin * 2);
  const availableHeight = Math.max(1, viewportSize.height - margin * 2);
  const zoom = Math.min(availableWidth / Math.max(1, content.width), availableHeight / Math.max(1, content.height));
  return {
    zoom,
    panX: (viewportSize.width - content.width * zoom) / 2 - content.x * zoom,
    panY: (viewportSize.height - content.height * zoom) / 2 - content.y * zoom,
  };
}

// ── Absolute bounds ──────────────────────────────────────────────────────────

/** A node's rect in ROOT document space, given its ancestors' offsets. */
export function absoluteRect(node: DesignNode, ancestors: DesignNode[]): Rect {
  let x = node.x;
  let y = node.y;
  for (const ancestor of ancestors) {
    x += ancestor.x;
    y += ancestor.y;
  }
  return { x, y, width: node.width, height: node.height };
}

export function rectOf(node: DesignNode): Rect {
  return { x: node.x, y: node.y, width: node.width, height: node.height };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

export function rectContainsPoint(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

/** Normalise a drag rect so width/height are never negative. */
export function normalizeRect(from: Point, to: Point): Rect {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  };
}

// ── Resize ───────────────────────────────────────────────────────────────────

export interface ResizeOptions {
  /** Shift — preserve the starting aspect ratio. */
  keepRatio?: boolean;
  /** Alt — resize symmetrically about the node's centre. */
  fromCenter?: boolean;
}

/**
 * The rect produced by dragging `handle` by (`dx`, `dy`) in document px.
 *
 * The opposite corner stays anchored (unless `fromCenter`), which is what makes
 * dragging the NW handle feel like it "pulls" the top-left rather than moving
 * the whole node.
 */
export function resizeRect(
  start: Rect,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  options: ResizeOptions = {},
): Rect {
  const movesLeft = handle.includes('w');
  const movesRight = handle.includes('e');
  const movesTop = handle.includes('n');
  const movesBottom = handle.includes('s');

  let { x, y, width, height } = start;

  if (options.fromCenter) {
    // Both edges move, so the centre is fixed and the delta counts twice.
    if (movesLeft) { x = start.x + dx; width = start.width - dx * 2; }
    if (movesRight) { x = start.x - dx; width = start.width + dx * 2; }
    if (movesTop) { y = start.y + dy; height = start.height - dy * 2; }
    if (movesBottom) { y = start.y - dy; height = start.height + dy * 2; }
  } else {
    if (movesLeft) { x = start.x + dx; width = start.width - dx; }
    if (movesRight) { width = start.width + dx; }
    if (movesTop) { y = start.y + dy; height = start.height - dy; }
    if (movesBottom) { height = start.height + dy; }
  }

  if (options.keepRatio && start.width > 0 && start.height > 0) {
    const ratio = start.width / start.height;
    // Follow whichever axis the user pulled further, so the drag feels direct.
    if (Math.abs(width - start.width) >= Math.abs(height - start.height)) {
      const nextHeight = width / ratio;
      if (movesTop) y += height - nextHeight;
      height = nextHeight;
    } else {
      const nextWidth = height * ratio;
      if (movesLeft) x += width - nextWidth;
      width = nextWidth;
    }
  }

  // Clamp without letting the anchored edge drift.
  if (width < MIN_NODE_SIZE) {
    if (movesLeft) x -= MIN_NODE_SIZE - width;
    width = MIN_NODE_SIZE;
  }
  if (height < MIN_NODE_SIZE) {
    if (movesTop) y -= MIN_NODE_SIZE - height;
    height = MIN_NODE_SIZE;
  }

  return { x: roundGeometry(x), y: roundGeometry(y), width: roundGeometry(width), height: roundGeometry(height) };
}

export const RESIZE_HANDLES: readonly ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export const HANDLE_CURSORS: Record<ResizeHandle, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
};

/** Handle position as a 0..1 fraction of the node box, for CSS placement. */
export function handleOffset(handle: ResizeHandle): Point {
  return {
    x: handle.includes('w') ? 0 : handle.includes('e') ? 1 : 0.5,
    y: handle.includes('n') ? 0 : handle.includes('s') ? 1 : 0.5,
  };
}

// ── Snapping ─────────────────────────────────────────────────────────────────

/** Geometry precision: one decimal place (450.4 × 106.5). Every write of a
 *  node's x / y / width / height goes through this instead of Math.round so
 *  inspector edits, drags and resizes keep sub-pixel sizes; a tenth of a
 *  pixel is the finest a designer can tell apart and keeps saved documents
 *  free of float noise like 450.40000000000003. */
export function roundGeometry(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * A box (parent-relative) made to fit inside a `container` of the given size: shrunk -
 * keeping its proportions - when larger than the room inside the padding, then moved in
 * from any edge it crosses. A box that already fits is returned unchanged.
 */
export function fitRectInside(rect: Rect, container: { width: number; height: number }, padding = 8): Rect {
  const roomWidth = Math.max(MIN_NODE_SIZE, container.width - padding * 2);
  const roomHeight = Math.max(MIN_NODE_SIZE, container.height - padding * 2);
  const scale = Math.min(1, roomWidth / rect.width, roomHeight / rect.height);
  const width = roundGeometry(Math.max(MIN_NODE_SIZE, rect.width * scale));
  const height = roundGeometry(Math.max(MIN_NODE_SIZE, rect.height * scale));
  const clamp = (value: number, size: number, extent: number): number =>
    roundGeometry(Math.min(Math.max(value, padding), Math.max(padding, extent - padding - size)));
  return { x: clamp(rect.x, width, container.width), y: clamp(rect.y, height, container.height), width, height };
}

export function snapToGrid(value: number, grid: number): number {
  return grid > 0 ? Math.round(value / grid) * grid : value;
}

export interface SnapResult {
  rect: Rect;
  guides: AlignmentGuide[];
}

/**
 * Snap `rect` to nearby `targets` (sibling rects plus the parent frame), then
 * fall back to the grid on any axis that found no alignment.
 *
 * The threshold is given in SCREEN px and divided by the zoom, so snapping
 * feels equally sticky whether you are at 25% or 200%.
 */
export function snapRect(
  rect: Rect,
  targets: Rect[],
  zoom: number,
  grid: { size: number; snap: boolean },
  thresholdPx = SNAP_THRESHOLD_PX,
): SnapResult {
  const threshold = thresholdPx / Math.max(zoom, 0.0001);
  const guides: AlignmentGuide[] = [];

  const xCandidates = edgeCandidates(rect, 'x');
  const yCandidates = edgeCandidates(rect, 'y');

  const bestX = bestSnap(xCandidates, targets, 'x', threshold);
  const bestY = bestSnap(yCandidates, targets, 'y', threshold);

  let { x, y } = rect;

  if (bestX) {
    x = rect.x + bestX.delta;
    guides.push({
      axis: 'x',
      at: bestX.at,
      from: Math.min(rect.y, bestX.target.y),
      to: Math.max(rect.y + rect.height, bestX.target.y + bestX.target.height),
    });
  } else if (grid.snap) {
    x = snapToGrid(rect.x, grid.size);
  }

  if (bestY) {
    y = rect.y + bestY.delta;
    guides.push({
      axis: 'y',
      at: bestY.at,
      from: Math.min(rect.x, bestY.target.x),
      to: Math.max(rect.x + rect.width, bestY.target.x + bestY.target.width),
    });
  } else if (grid.snap) {
    y = snapToGrid(rect.y, grid.size);
  }

  return { rect: { ...rect, x: roundGeometry(x), y: roundGeometry(y) }, guides };
}

/** The three lines a rect can snap BY on one axis: start, centre, end. */
function edgeCandidates(rect: Rect, axis: 'x' | 'y'): number[] {
  const start = axis === 'x' ? rect.x : rect.y;
  const size = axis === 'x' ? rect.width : rect.height;
  return [start, start + size / 2, start + size];
}

interface SnapCandidate {
  delta: number;
  at: number;
  target: Rect;
}

function bestSnap(
  candidates: number[],
  targets: Rect[],
  axis: 'x' | 'y',
  threshold: number,
): SnapCandidate | null {
  let best: SnapCandidate | null = null;
  for (const target of targets) {
    for (const line of edgeCandidates(target, axis)) {
      for (const candidate of candidates) {
        const delta = line - candidate;
        if (Math.abs(delta) > threshold) continue;
        if (!best || Math.abs(delta) < Math.abs(best.delta)) {
          best = { delta, at: line, target };
        }
      }
    }
  }
  return best;
}

// ── Alignment / distribution ─────────────────────────────────────────────────

export type AlignMode = 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom';

/** The new x/y for each node when aligning a multi-selection to its bounds. */
export function alignRects(rects: Rect[], mode: AlignMode): Point[] {
  if (rects.length < 2) return rects.map((rect) => ({ x: rect.x, y: rect.y }));
  const minX = Math.min(...rects.map((r) => r.x));
  const maxX = Math.max(...rects.map((r) => r.x + r.width));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxY = Math.max(...rects.map((r) => r.y + r.height));
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return rects.map((rect) => {
    switch (mode) {
      case 'left': return { x: minX, y: rect.y };
      case 'right': return { x: maxX - rect.width, y: rect.y };
      case 'center-h': return { x: roundGeometry(centerX - rect.width / 2), y: rect.y };
      case 'top': return { x: rect.x, y: minY };
      case 'bottom': return { x: rect.x, y: maxY - rect.height };
      case 'center-v': return { x: rect.x, y: roundGeometry(centerY - rect.height / 2) };
    }
  });
}

/**
 * Even gaps along one axis, keeping the first and last node fixed. Returns the
 * new positions in the SAME order as the input (not sorted), so callers can zip
 * them straight back onto their nodes.
 */
export function distributeRects(rects: Rect[], axis: 'x' | 'y'): Point[] {
  if (rects.length < 3) return rects.map((rect) => ({ x: rect.x, y: rect.y }));

  const sizeKey = axis === 'x' ? 'width' : 'height';
  const indexed = rects.map((rect, index) => ({ rect, index }));
  indexed.sort((a, b) => a.rect[axis] - b.rect[axis]);

  const first = indexed[0].rect;
  const last = indexed[indexed.length - 1].rect;
  const span = last[axis] + last[sizeKey] - first[axis];
  const occupied = indexed.reduce((sum, item) => sum + item.rect[sizeKey], 0);
  const gap = (span - occupied) / (indexed.length - 1);

  const result: Point[] = new Array(rects.length);
  let cursor = first[axis];
  for (const { rect, index } of indexed) {
    result[index] = axis === 'x' ? { x: roundGeometry(cursor), y: rect.y } : { x: rect.x, y: roundGeometry(cursor) };
    cursor += rect[sizeKey] + gap;
  }
  return result;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
